import { DriverInvoiceResumeMonthOutput } from '@allocab/allocab-driver-shared-types';
import { CalendarDaysSolid, CircleCheckSolid } from '@allocab/allocab-icons';

import { StatCard } from '../../components/cards/StatCard';
import { t, tPlural } from '../../services/translation/translation';
import { formatDateForParisTZ } from '../../utils/date';
import { formatPrice } from '../../utils/price';

type Props = {
  data?: DriverInvoiceResumeMonthOutput;
  isFetching: boolean;
};

export const MonthlyInvoiceTotal = ({ isFetching, data }: Props) => {
  if (!data) {
    return null;
  }

  const firstResumeTotal =
    data.firstResume.totalFareIn - data.firstResume.totalComOut - data.firstResume.totalOnBoardSales;
  const secondResumeTotal =
    data.secondResume.totalFareIn - data.secondResume.totalComOut - data.secondResume.totalOnBoardSales;

  return (
    <div className="gap-4 grid lg:grid-cols-4 grid-cols-2 text-center">
      <StatCard
        text={formatPrice(firstResumeTotal + secondResumeTotal)}
        caption={
          <>
            <span className="flex items-center gap-1">
              {data.firstResume.payed ? (
                <CircleCheckSolid width={12} height={12} className="fill-green-700 dark:fill-green-600" />
              ) : (
                <CalendarDaysSolid width={12} height={12} className="fill-current" />
              )}
              {formatDateForParisTZ(data.firstResume.paymentDate, t('date.date-short'), false) + ': '}
              <span className="font-bold">{formatPrice(firstResumeTotal)}</span>
            </span>
            <span className="flex items-center gap-1">
              {data.secondResume.payed ? (
                <CircleCheckSolid width={12} height={12} className="fill-green-700 dark:fill-green-600" />
              ) : (
                <CalendarDaysSolid width={12} height={12} className="fill-current" />
              )}
              {formatDateForParisTZ(data.secondResume.paymentDate, t('date.date-short'), false) + ': '}
              <span className="font-bold">{formatPrice(secondResumeTotal)}</span>
            </span>
          </>
        }
        isFetching={isFetching}
      />
      <StatCard
        text={formatPrice(data.firstResume.totalFareIn + data.secondResume.totalFareIn)}
        caption={
          <span className="flex flex-col">
            <span>
              {t('invoicing-route.total-tolls', {
                price: formatPrice(data.firstResume.totalTolls + data.secondResume.totalTolls),
              })}
            </span>
            <span>{tPlural('invoicing-route.invoice-sent', { count: data.invoiceIn.length })}</span>
          </span>
        }
        isFetching={isFetching}
      />
      <StatCard
        text={formatPrice((data.firstResume.totalComOut + data.secondResume.totalComOut) * -1)}
        caption={tPlural('invoicing-route.invoice-received', { count: data.invoiceOut.length })}
        isFetching={isFetching}
      />
      <StatCard
        text={formatPrice(data.firstResume.totalOnBoardSales + data.secondResume.totalOnBoardSales)}
        caption={t('invoicing-route.onboard-sales')}
        isFetching={isFetching}
      />
    </div>
  );
};
